import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { addUserBySuperAdmin, updateUserBySuperAdmin, getAllRightsEdit, getUserRightsEdit } from "../../services/api/SuperAdminApi";
import './SAUserForm.scss';
import { getMembers } from "../../services/api/MemberApi";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import { useLoading } from "../../context/LoadingContext";

export const SAUserForm = () => {
    const { id } = useParams();
    const { company } = useAuth();
    const { showToast } = useToast();
    const { showLoading, hideLoading } = useLoading();
    const navigate = useNavigate();
    const [companiesList, setCompaniesList] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [companySearch, setCompanySearch] = useState('');
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const companySearchRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role: 'lawyer',
        companyId: company?._id || '',
        phno: '',
        isActive: true,
        visibility: {}
    });
    const [visibilityRights, setVisibilityRights] = useState({});
    const [isLoadingRights, setIsLoadingRights] = useState(false);

    // Function to fetch company rights based on company ID (for new users)
    const fetchCompanyRights = useCallback(async (companyId) => {
        if (!companyId) {
            setVisibilityRights({});
            setFormData(prev => ({ ...prev, visibility: {} }));
            return;
        }

        setIsLoadingRights(true);
        try {
            const response = await getAllRightsEdit(companyId);
            const rights = response.data || {};
            setVisibilityRights(rights);
            setFormData(prev => ({ ...prev, visibility: { ...rights } }));
        } catch (error) {
            console.error('Error fetching company rights:', error);
            setVisibilityRights({});
            setFormData(prev => ({ ...prev, visibility: {} }));
        } finally {
            setIsLoadingRights(false);
        }
    }, []);

    // Function to fetch user rights based on user ID (for editing users)
    const fetchUserRights = useCallback(async (userId) => {
        if (!userId) {
            setVisibilityRights({});
            setFormData(prev => ({ ...prev, visibility: {} }));
            return;
        }

        setIsLoadingRights(true);
        try {
            const response = await getUserRightsEdit(userId);
            const rights = response.data || {};
            setVisibilityRights(rights);
            setFormData(prev => ({ ...prev, visibility: { ...rights } }));
        } catch (error) {
            console.error('Error fetching user rights:', error);
            setVisibilityRights({});
            setFormData(prev => ({ ...prev, visibility: {} }));
        } finally {
            setIsLoadingRights(false);
        }
    }, []);

    useEffect(() => {
        if (id) {
            showLoading('Loading user details...');
            getMembers(id)
                .then(response => {
                    const userData = response.data;
                    setFormData(prev => ({
                        ...prev,
                        ...userData
                    }));
                    // Set selected company if editing
                    if (userData.companyId && companiesList.length > 0) {
                        const company = companiesList.find(c => c._id === userData.companyId);
                        if (company) {
                            setSelectedCompany(company);
                        }
                    }
                    // Fetch user rights when editing (instead of company rights)
                    fetchUserRights(id);
                })
                .catch(error => {
                    showToast('Failed to load user details.', 'error');
                    console.error('Error fetching user:', error);
                })
                .finally(() => hideLoading());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, companiesList, fetchUserRights]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'role') {
            setFormData(prev => {
                const { superAdmin: _removed, ...visibilityWithoutSuperAdmin } = prev.visibility;
                return {
                    ...prev,
                    role: value,
                    visibility: value === 'superAdmin'
                        ? { ...prev.visibility, superAdmin: true }
                        : visibilityWithoutSuperAdmin,
                };
            });
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleCompanySearch = (e) => {
        const value = e.target.value;
        setCompanySearch(value);
        setShowCompanyDropdown(true);
        
        // Clear selection if search is cleared
        if (!value && selectedCompany) {
            setSelectedCompany(null);
            setFormData(prev => ({ ...prev, companyId: '' }));
        }
    };

    const handleCompanySelect = (company) => {
        setSelectedCompany(company);
        setFormData(prev => ({ ...prev, companyId: company._id }));
        setCompanySearch('');
        setShowCompanyDropdown(false);
        
        // Fetch rights for the selected company
        fetchCompanyRights(company._id);
    };

    const clearCompanySelection = () => {
        setSelectedCompany(null);
        setFormData(prev => ({ ...prev, companyId: '', visibility: {} }));
        setCompanySearch('');
        setVisibilityRights({});
    };

    const toggleActiveStatus = () => {
        setFormData(prev => ({ ...prev, isActive: !prev.isActive }));
    };

    // Filter companies based on search
    const filteredCompanies = companiesList.filter(company =>
        company.companyName.toLowerCase().includes(companySearch.toLowerCase())
    );

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (companySearchRef.current && !companySearchRef.current.contains(event.target)) {
                setShowCompanyDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const isFormValid = () => {
        return formData.name?.trim() &&
            formData.email?.trim() &&
            formData.phno?.trim() &&
            formData.role?.trim() &&
            formData.companyId?.trim() &&
            /\S+@\S+\.\S+/.test(formData.email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid()) {
            showToast('Please fill in all required fields with valid information.', 'error');
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            let response;
            console.log('Form Data sauser:', formData);
            if (id) {
                response = await updateUserBySuperAdmin(id, formData);
            } else {
                response = await addUserBySuperAdmin(formData);
            }

            // Check if response contains an error code (e.g., duplicate)
            if (response.data?.code === "duplicate" || response.data?.message?.includes("already registered")) {
                showToast(response.data.message, "error");
                return;
            }

            if (response && response.data) {
                showToast(id ? 'User updated successfully!' : 'User added successfully!', 'success');
                showLoading('Redirecting...');
                setTimeout(() => {
                    navigate('/home/superadmin?tab=users');
                }, 1500);
            } else {
                showToast(id ? 'Failed to update user.' : 'Failed to add user.', 'error');
            }
        } catch (error) {
            console.error('Error saving user:', error);
            
            // Handle specific error messages from backend
            const errorMessage = error.response?.data?.message || 
                                error.response?.data?.error ||
                                (id ? 'Failed to update user. Please try again.' : 'Failed to add user. Please try again.');
            
            showToast(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        // Load companies from localStorage
        const storedCompanies = localStorage.getItem('companiesList');
        if (storedCompanies) {
            try {
                setCompaniesList(JSON.parse(storedCompanies));
            } catch (error) {
                console.error('Error parsing companies from localStorage:', error);
            }
        }
    }, []);

    return (
        <div className="sauser-form-container">
            <div className="sauser-form-wrapper">
                <div className="sauser-form-header">
                    <h1 className="sauser-form-title">{id ? 'Edit User' : 'Add New User'}</h1>
                </div>

                <hr className="sauser-form-divider" />

                <form onSubmit={handleSubmit} className="form-content">
                    {/* Basic Information Section */}
                    <div className="form-section">
                        {/* <h3>Basic Information</h3> */}

                        <div className="form-row">
                            <div className="form-group">
                                <label>Full Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Enter full name"
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div className="form-group">
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="user@company.com"
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Phone Number</label>
                                <input
                                    type="tel"
                                    name="phno"
                                    value={formData.phno}
                                    onChange={handleChange}
                                    placeholder="+91-9876543210"
                                    required
                                    disabled={isSubmitting}
                                />
                            </div>

                            <div className="form-group">
                                <label>Role</label>
                                <select
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                    required
                                    disabled={isSubmitting}
                                >
                                    <option value="lawyer">Lawyer</option>
                                    <option value="moderator">Moderator</option>
                                    <option value="admin">Admin</option>
                                    <option value="superAdmin">Super Admin</option>
                                    <option value="customer">Customer</option>
                                </select>
                            </div>
                        </div>

                        {/* Add new row for Company and IsActive */}
                        <div className="form-row">
                            <div className="form-group company-search" ref={companySearchRef}>
                                <label>Company</label>
                                {selectedCompany ? (
                                    <div className="selected-company">
                                        <span className="company-name">{selectedCompany.companyName}</span>
                                        <button 
                                            type="button" 
                                            className="clear-button"
                                            onClick={clearCompanySelection}
                                            disabled={isSubmitting}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="search-input">
                                            <input
                                                type="text"
                                                value={companySearch}
                                                onChange={handleCompanySearch}
                                                onFocus={() => setShowCompanyDropdown(true)}
                                                placeholder="Search and select company..."
                                                required
                                                disabled={isSubmitting}
                                            />
                                            {/* <span className="search-icon">🔍</span> */}
                                        </div>
                                        {showCompanyDropdown && (
                                            <div className="dropdown-list">
                                                {filteredCompanies.length > 0 ? (
                                                    filteredCompanies.map(company => (
                                                        <div
                                                            key={company._id}
                                                            className="dropdown-item"
                                                            onClick={() => handleCompanySelect(company)}
                                                        >
                                                            {company.companyName}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="dropdown-item no-results">
                                                        No companies found
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="form-group toggle-group">
                                <label>Active User</label>
                                <button
                                    type="button"
                                    className={`toggle-switch ${formData.isActive ? 'active' : ''}`}
                                    onClick={toggleActiveStatus}
                                    disabled={isSubmitting}
                                    aria-pressed={formData.isActive}
                                    aria-label="Toggle user active status"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Module Visibility Section */}
                    {(selectedCompany || id) && !(id && formData.role === 'customer') && (
                        <div className="form-section">
                            <div className="visibility-section">
                                <h3>Module Visibility</h3>
                                {isLoadingRights ? (
                                    <div className="loading-rights">
                                        <span>{id ? 'Loading user rights...' : 'Loading company rights...'}</span>
                                    </div>
                                ) : Object.keys(visibilityRights).length > 0 ? (
                                    <div className="visibility-toggles">
                                        {Object.entries(visibilityRights).map(([key, value]) => (
                                            <label key={key} className="toggle-label">
                                                {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                                                <div className="toggle-switch">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.visibility[key] ?? value}
                                                        onChange={() =>
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                visibility: {
                                                                    ...prev.visibility,
                                                                    [key]: !prev.visibility[key],
                                                                },
                                                            }))
                                                        }
                                                        disabled={isSubmitting}
                                                    />
                                                    <span className="slider" />
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="no-rights">
                                        <span>{id ? 'No visibility rights available for this user.' : 'No visibility rights available for this company.'}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Submit Button */}
                    <button type="submit" disabled={!isFormValid() || isSubmitting}>
                        {isSubmitting 
                            ? (id ? 'Updating...' : 'Saving...') 
                            : (id ? 'Update User' : 'Save User')
                        }
                    </button>
                </form>
            </div>
        </div>
    );
};